const DATE_INPUT_RE = /^(\d{4})-(\d{2})-(\d{2})/;

export type DateRangeAnchor = 'start' | 'end' | 'none';

export interface DateRange {
  startDate: string;
  endDate: string;
}

export function formatApiDateForInput(value: string | null | undefined): string {
  if (!value) return '';

  const match = DATE_INPUT_RE.exec(value);
  if (match) {
    return `${match[1]}-${match[2]}-${match[3]}`;
  }

  const dateObj = new Date(value);
  if (Number.isNaN(dateObj.getTime())) return '';
  return dateObj.toISOString().slice(0, 10);
}

/** Add days to a YYYY-MM-DD input value in UTC (negative to subtract). */
export function addDaysToDateInput(value: string, days: number): string {
  const input = formatApiDateForInput(value);
  if (!input) return '';

  const date = new Date(`${input}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

/** Today as a UTC calendar day (YYYY-MM-DD) — the day the readings lambdas
 *  bound their queries by. */
export function todayUtcDateInput(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** Whole days from one YYYY-MM-DD to another (negative when `to` is earlier),
 *  or null when either is not a date. */
export function daysBetweenDateInputs(from: string, to: string): number | null {
  const a = Date.parse(`${formatApiDateForInput(from)}T00:00:00Z`);
  const b = Date.parse(`${formatApiDateForInput(to)}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((b - a) / 86400000);
}

export function formatDateInputForDisplay(value: string): string {
  const inputValue = formatApiDateForInput(value);
  if (!inputValue) return '';

  const [year, month, day] = inputValue.split('-');
  return `${month}/${day}/${year}`;
}

export function isValidDateRange(startDate: string, endDate: string): boolean {
  return Boolean(startDate && endDate && startDate <= endDate);
}

/** Processed-day window for a device+analysis, from analysis metadata.
 *  `oldest`/`latest` empty string = unknown; `availableDates` (YYYY-MM-DD,
 *  ascending) null = unknown, [] = no processed days yet — resolveDateRange
 *  is permissive in both cases. */
export interface DateRangeBounds {
  oldest: string;
  latest: string;
  availableDates: string[] | null;
}

/** Snap a date to the nearest processed day. 'start' prefers the next
 *  available day (forward into the range), 'end' the previous one, so a
 *  snapped range never excludes the data the user aimed at. ISO date
 *  strings compare correctly as strings. */
export function snapToAvailableDate(
  value: string,
  availableDates: string[] | null,
  edge: 'start' | 'end',
): string {
  if (!value || !availableDates || availableDates.length === 0) return value;
  if (availableDates.includes(value)) return value;
  if (edge === 'start') {
    return availableDates.find((d) => d > value)
      ?? availableDates[availableDates.length - 1];
  }
  const before = availableDates.filter((d) => d < value);
  return before.length > 0 ? before[before.length - 1] : availableDates[0];
}

/** Resolve the range to actually use: default to the full processed window
 *  when nothing was requested, clamp into [oldest, latest], snap both edges
 *  to processed days, and normalize the order. A range entirely outside the
 *  window falls back to the full window. Deriving the effective range this
 *  way (instead of clamping at event time) lets the metadata and
 *  filter-period responses land in any order without stranding an
 *  out-of-window date.
 *
 *  Bounds that are known but carry no window at all — no oldest, no latest,
 *  no processed days — are treated like unknown bounds: the request passes
 *  through and the fallback date is the default. That is a fresh install
 *  (readings exist, daily_calcs does not); clamping into a window that does
 *  not exist pinned every typed date to the fallback day. */
export function resolveDateRange(
  requested: DateRange | null,
  bounds: DateRangeBounds | null,
  fallbackDate: string,
): DateRange {
  if (!bounds || (!bounds.oldest && !bounds.latest && !bounds.availableDates?.length)) {
    return normalizeDateRange(
      requested?.startDate || fallbackDate,
      requested?.endDate || fallbackDate,
    );
  }
  const lo = bounds.oldest || bounds.latest || fallbackDate;
  const hi = bounds.latest || bounds.oldest || fallbackDate;
  const start = requested?.startDate || lo;
  const end = requested?.endDate || hi;
  const clampedStart = start < lo ? lo : (start > hi ? lo : start);
  const clampedEnd = end > hi ? hi : (end < lo ? hi : end);
  return normalizeDateRange(
    snapToAvailableDate(clampedStart, bounds.availableDates, 'start'),
    snapToAvailableDate(clampedEnd, bounds.availableDates, 'end'),
  );
}

export function normalizeDateRange(
  startDate: string,
  endDate: string,
  anchor: DateRangeAnchor = 'none',
): DateRange {
  let normalizedStart = formatApiDateForInput(startDate);
  let normalizedEnd = formatApiDateForInput(endDate);

  if (!normalizedStart && normalizedEnd) normalizedStart = normalizedEnd;
  if (normalizedStart && !normalizedEnd) normalizedEnd = normalizedStart;

  if (normalizedStart && normalizedEnd && normalizedStart > normalizedEnd) {
    if (anchor === 'start') {
      normalizedEnd = normalizedStart;
    } else if (anchor === 'end') {
      normalizedStart = normalizedEnd;
    } else {
      [normalizedStart, normalizedEnd] = [normalizedEnd, normalizedStart];
    }
  }

  return { startDate: normalizedStart, endDate: normalizedEnd };
}
